import type { AutocompleteOption } from '@talentmesh/ui/components/autocomplete-chips';
import { AutocompleteChips } from '@talentmesh/ui/components/autocomplete-chips';
import type { GeocodeResponse } from '../../../../../api/DTO/GeocodeResponse';
import useGeocode from '../../../../../api/useGeocode';
import { useRefineSearchFiltersFormContext } from '../../useRefineSearchFiltersForm';
import UIStrings from '@/Utils/UIStrings';

export interface LocationAutocompleteOptions {
    cities: AutocompleteOption[];
    regions: AutocompleteOption[];
    countries: AutocompleteOption[];
}

interface LocationAutocompleteChipsProps {
    getAutocompleteOptions: (search: string) => Promise<LocationAutocompleteOptions>;
}

function withGroups(response: LocationAutocompleteOptions): AutocompleteOption[] {
    return [
        ...response.cities.map((opt) => ({ ...opt, group: UIStrings.Cities })),
        ...response.regions.map((opt) => ({ ...opt, group: UIStrings.Regions })),
        ...response.countries.map((opt) => ({ ...opt, group: UIStrings.Countries })),
    ];
}

function locationChipLabel(chip: unknown): string {
    const row = chip as GeocodeResponse & { id?: string };
    return row.localityName || row.regionName || row.country || '';
}

const LocationAutocompleteChips = ({ getAutocompleteOptions }: LocationAutocompleteChipsProps) => {
    const form = useRefineSearchFiltersFormContext();
    const { isMutating, trigger } = useGeocode();

    return (
        <AutocompleteChips
            name="locations"
            placeholder={UIStrings.AddLocation}
            limit={100}
            disabled={form.formState.disabled || isMutating}
            pending={isMutating}
            dataCy="locations.autocomplete"
            getChipLabel={locationChipLabel}
            getAutocompleteOptions={async (search: string) => {
                if (!search) return [];
                const response = await getAutocompleteOptions(search);
                return withGroups(response);
            }}
            onOptionSelect={async (option, append) => {
                const geocode = await trigger(option.label);
                const locations = (form.getValues('locations') ?? []) as GeocodeResponse[];
                if (locations.some((l) => l.placeId === geocode.placeId)) return;
                append(geocode);
            }}
        />
    );
};

export default LocationAutocompleteChips;
